import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import { Link } from "wouter";
import { TownSelect } from "@/components/TownSelect";
import { ImageUpload } from "@/components/ImageUpload";
import {
  Building2,
  Clock,
  Check,
  ChevronLeft,
  ChevronRight,
  Music,
  HelpCircle,
  Mic,
  Star,
  Users,
  Smile,
  Headphones,
} from "lucide-react";

// ── Types ──────────────────────────────────────────────────────────────

type DayHours = {
  closed: boolean;
  open: string;
  close: string;
};

type RegularEvent = {
  enabled: boolean;
  frequency: "weekly" | "fortnightly" | "monthly";
  days: number[]; // 0=Sun … 6=Sat
  time: string;
  notes: string;
  priceText: string;
};

// ── Constants ──────────────────────────────────────────────────────────

const VENUE_TYPES = [
  { value: "pub", label: "Pub" },
  { value: "bar", label: "Bar" },
  { value: "restaurant", label: "Restaurant" },
  { value: "nightclub", label: "Nightclub" },
  { value: "sports_bar", label: "Sports Bar" },
  { value: "cocktail_bar", label: "Cocktail Bar" },
];

const DAY_NAMES = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const DEFAULT_HOURS: DayHours[] = [
  { closed: false, open: "11:00", close: "23:00" }, // Mon
  { closed: false, open: "11:00", close: "23:00" }, // Tue
  { closed: false, open: "11:00", close: "23:00" }, // Wed
  { closed: false, open: "11:00", close: "23:00" }, // Thu
  { closed: false, open: "11:00", close: "00:00" }, // Fri
  { closed: false, open: "11:00", close: "00:00" }, // Sat
  { closed: false, open: "12:00", close: "22:30" }, // Sun
];

const AMENITY_OPTIONS: { key: string; label: string }[] = [
  { key: "pool_table", label: "Pool Table" },
  { key: "darts", label: "Darts Board" },
  { key: "jukebox", label: "Jukebox" },
  { key: "beer_garden", label: "Beer Garden" },
  { key: "sports_tv", label: "Sports TV / Sky Sports" },
  { key: "ufc_boxing", label: "UFC / Boxing Shown" },
  { key: "karaoke_rig", label: "Karaoke Equipment" },
  { key: "stage", label: "Live Stage / PA System" },
  { key: "live_sport", label: "Live Sport Shown" },
  { key: "outdoor_area", label: "Outdoor Area" },
  { key: "private_hire", label: "Available for Private Hire" },
  { key: "dog_friendly", label: "Dog Friendly" },
  { key: "food", label: "Food Served" },
];

const EVENT_DEFINITIONS: { key: string; label: string; icon: React.ReactNode }[] = [
  { key: "quiz_night", label: "Quiz Night", icon: <HelpCircle size={15} /> },
  { key: "karaoke_night", label: "Karaoke Night", icon: <Mic size={15} /> },
  { key: "live_music", label: "Live Music", icon: <Music size={15} /> },
  { key: "open_mic", label: "Open Mic Night", icon: <Mic size={15} /> },
  { key: "bingo", label: "Bingo", icon: <Star size={15} /> },
  { key: "comedy_night", label: "Comedy Night", icon: <Smile size={15} /> },
  { key: "dj_club_night", label: "DJ / Club Night", icon: <Headphones size={15} /> },
];

const SHORT_DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Build time options: 00:00 to 03:00 in 30-min steps, rest of day 00:30 to 23:30
function buildTimeOptions(): string[] {
  const times: string[] = [];
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 30]) {
      const hh = String(h).padStart(2, "0");
      const mm = String(m).padStart(2, "0");
      times.push(`${hh}:${mm}`);
    }
  }
  // Add early-morning closing times 00:00 to 03:00
  for (let h = 0; h <= 3; h++) {
    for (const m of h < 3 ? [0, 30] : [0]) {
      const hh = String(h).padStart(2, "0");
      const mm = String(m).padStart(2, "0");
      const t = `${hh}:${mm}`;
      if (!times.includes(t)) times.push(t);
    }
  }
  return times;
}

const TIME_OPTIONS = buildTimeOptions();

const DEFAULT_EVENTS: Record<string, RegularEvent> = Object.fromEntries(
  EVENT_DEFINITIONS.map((e) => [
    e.key,
    {
      enabled: false,
      frequency: "weekly",
      days: [],
      time: "19:00",
      notes: "",
      priceText: "",
    },
  ])
);

// ── Step indicator ─────────────────────────────────────────────────────

function StepDots({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex items-center gap-1.5">
      {Array.from({ length: total }).map((_, i) => (
        <span
          key={i}
          className={`h-2 rounded-full transition-all ${
            i === current ? "w-6 bg-white" : "w-2 bg-white/40"
          }`}
        />
      ))}
    </div>
  );
}

// ── Time select ────────────────────────────────────────────────────────

function TimeSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="text-sm text-slate-700 bg-transparent outline-none cursor-pointer"
    >
      {TIME_OPTIONS.map((t) => (
        <option key={t} value={t}>
          {t}
        </option>
      ))}
    </select>
  );
}

// ── Section wrapper ────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <p className="text-sm font-bold text-slate-700">{title}</p>
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {children}
      </div>
    </div>
  );
}

function FormField({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="px-4 py-3">
      <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block mb-1.5">
        {label}
        {required && " *"}
      </label>
      {children}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────

export default function ListYourVenue() {
  const { user } = useAuth();

  // Navigation
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Step 1 state
  const [name, setName] = useState("");
  const [venueType, setVenueType] = useState("pub");
  const [town, setTown] = useState("Ayr");
  const [address, setAddress] = useState("");
  const [postcode, setPostcode] = useState("");
  const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState("");
  const [description, setDescription] = useState("");
  const [coverPhoto, setCoverPhoto] = useState("");
  const [imageUploading, setImageUploading] = useState(false);

  // Step 2 state
  const [openingHours, setOpeningHours] = useState<DayHours[]>(
    DEFAULT_HOURS.map((d) => ({ ...d }))
  );

  // Step 3 state
  const [amenities, setAmenities] = useState<Set<string>>(new Set());
  const [events, setEvents] = useState<Record<string, RegularEvent>>(
    JSON.parse(JSON.stringify(DEFAULT_EVENTS))
  );

  // Existing businesses (for linking)
  const [myBusinesses, setMyBusinesses] = useState<{ id: number; name: string; town: string | null; address: string | null; phone: string | null; website: string | null; imageUrl: string | null }[]>([]);
  const [linkedBusinessId, setLinkedBusinessId] = useState<number | null>(null);

  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem("ayrshire_token");
    fetch("/api/my-businesses", { headers: { Authorization: `Bearer ${token ?? ""}` } })
      .then(r => r.json())
      .then((d: { items: { id: number; name: string; town: string | null; address: string | null; phone: string | null; website: string | null; imageUrl: string | null }[] }) => setMyBusinesses(d.items ?? []))
      .catch(() => {});
  }, [user]);

  function linkBusiness(id: number | null) {
    setLinkedBusinessId(id);
    if (id === null) return;
    const biz = myBusinesses.find(b => b.id === id);
    if (!biz) return;
    setName(biz.name);
    if (biz.town) setTown(biz.town);
    if (biz.address) setAddress(biz.address);
    if (biz.phone) setPhone(biz.phone);
    if (biz.website) setWebsite(biz.website);
    if (biz.imageUrl) setCoverPhoto(biz.imageUrl);
  }

  // ── Auth guard ────────────────────────────────────────────────────

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-[70vh] bg-slate-50 px-4">
        <div className="w-full max-w-sm bg-white rounded-3xl border border-slate-100 shadow-sm p-7 text-center space-y-5">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto">
            <Building2 size={28} className="text-primary/70" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-800">List Your Venue</h2>
            <p className="text-sm text-slate-500 mt-2 leading-relaxed">
              You need to be signed in to list your venue on Ayrshire Hub.
            </p>
          </div>
          <Link href="/login">
            <button className="w-full bg-primary hover:bg-primary/90 text-white font-bold py-4 rounded-2xl transition-colors">
              Sign In
            </button>
          </Link>
          <Link href="/signup">
            <button className="w-full border border-slate-200 text-slate-600 py-3.5 rounded-2xl text-sm hover:bg-slate-50 transition-colors">
              Create an account
            </button>
          </Link>
        </div>
      </div>
    );
  }

  // ── Success state ────────────────────────────────────────────────

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-start justify-center px-4 py-12">
        <div className="w-full max-w-sm space-y-5">
          <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-7 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
              <Check size={32} className="text-emerald-500" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">Venue Submitted!</h2>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed">
                Your venue has been submitted for review. We'll have it live within 24 hours.
              </p>
            </div>
          </div>
          <Link href="/my-venues">
            <button className="w-full bg-primary text-white font-semibold py-3.5 rounded-2xl text-sm hover:bg-primary/90 transition-colors">
              Manage My Venues
            </button>
          </Link>
          <Link href="/whats-on">
            <button className="w-full border border-slate-200 text-slate-600 py-3.5 rounded-2xl text-sm hover:bg-slate-50 transition-colors">
              Browse What's On
            </button>
          </Link>
        </div>
      </div>
    );
  }

  // ── Helpers ───────────────────────────────────────────────────────

  function updateHours(index: number, patch: Partial<DayHours>) {
    setOpeningHours((prev) =>
      prev.map((d, i) => (i === index ? { ...d, ...patch } : d))
    );
  }

  function toggleAmenity(key: string) {
    setAmenities((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function updateEvent(key: string, patch: Partial<RegularEvent>) {
    setEvents((prev) => ({
      ...prev,
      [key]: { ...prev[key]!, ...patch },
    }));
  }

  function toggleEventDay(key: string, dayIndex: number) {
    setEvents((prev) => {
      const ev = prev[key]!;
      const days = ev.days.includes(dayIndex)
        ? ev.days.filter((d) => d !== dayIndex)
        : [...ev.days, dayIndex];
      return { ...prev, [key]: { ...ev, days } };
    });
  }

  // ── Submit ────────────────────────────────────────────────────────

  async function handleSubmit() {
    if (!name.trim()) {
      alert("Please enter a venue name.");
      setStep(0);
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem("ayrshire_token");
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      // 1. Create venue via new endpoint
      const barRes = await fetch("/api/venues", {
        method: "POST",
        headers,
        body: JSON.stringify({
          name,
          venueType,
          town,
          address,
          postcode,
          phone,
          website,
          description,
          coverPhoto: coverPhoto || undefined,
          openingHours,
          amenities: Array.from(amenities),
          businessId: linkedBusinessId ?? undefined,
        }),
      });

      if (!barRes.ok) {
        const err = await barRes.json().catch(() => ({}));
        throw new Error((err as { message?: string }).message ?? "Failed to create venue");
      }

      const { id: newBarId } = (await barRes.json()) as { id: number };

      // 2. Post recurring activities for each enabled event × each selected day
      const activityPromises: Promise<void>[] = [];
      for (const def of EVENT_DEFINITIONS) {
        const ev = events[def.key]!;
        if (!ev.enabled || ev.days.length === 0) continue;
        for (const dayOfWeek of ev.days) {
          activityPromises.push(
            fetch(`/api/venues/${newBarId}/activities`, {
              method: "POST",
              headers,
              body: JSON.stringify({
                title: def.label,
                category: ({ quiz_night: "quiz_night", karaoke_night: "karaoke", live_music: "live_music", open_mic: "open_mic", bingo: "bingo", comedy_night: "comedy", dj_club_night: "dj" } as Record<string,string>)[def.key] ?? def.key,
                frequency: ev.frequency,
                dayOfWeek,
                startTime: ev.time,
                priceText: ev.priceText,
                description: ev.notes,
              }),
            }).then(() => undefined)
          );
        }
      }
      await Promise.all(activityPromises);

      setSubmitted(true);
    } catch (err) {
      alert(
        err instanceof Error ? err.message : "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  }

  // ── Step labels ───────────────────────────────────────────────────

  const STEP_LABELS = ["Basic Details", "Opening Hours", "What You Offer"];

  // ── Render ────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-primary px-4 py-6">
        <div className="max-w-xl mx-auto">
          <button
            onClick={() => (step > 0 ? setStep(step - 1) : window.history.back())}
            className="flex items-center gap-1 text-sm text-white/80 hover:text-white mb-3 transition-colors"
          >
            <ChevronLeft size={18} /> {step > 0 ? "Back" : "Cancel"}
          </button>
        </div>
        <div className="max-w-xl mx-auto flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <Building2 size={22} className="text-white" />
          </div>
          <div className="flex-1">
            <h1 className="text-white font-bold text-xl">List Your Venue</h1>
            <p className="text-white/70 text-sm">
              Step {step + 1} of 3 — {STEP_LABELS[step]}
            </p>
          </div>
          <StepDots current={step} total={3} />
        </div>
      </div>

      <div className="max-w-xl mx-auto px-4 py-6 space-y-6">
        {/* ── STEP 1: Basic Details ── */}
        {step === 0 && (
          <>
            {/* Link to existing business */}
            {myBusinesses.length > 0 && (
              <Section title="Link to Your Business Listing">
                <div className="px-4 py-3 space-y-2">
                  <p className="text-xs text-slate-500 leading-relaxed">
                    You have businesses in the directory. Select one to register it as your venue — we'll pre-fill the details for you.
                  </p>
                  <select
                    value={linkedBusinessId ?? ""}
                    onChange={(e) => linkBusiness(e.target.value ? Number(e.target.value) : null)}
                    className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 outline-none focus:border-primary/50 bg-white appearance-none cursor-pointer"
                  >
                    <option value="">— Register a brand new venue —</option>
                    {myBusinesses.map(b => (
                      <option key={b.id} value={b.id}>{b.name}{b.town ? ` (${b.town})` : ""}</option>
                    ))}
                  </select>
                  {linkedBusinessId && (
                    <p className="text-xs text-emerald-600 font-medium">✓ Details pre-filled from your business listing. You can edit them below.</p>
                  )}
                </div>
              </Section>
            )}

            <Section title="Venue Details">
              <FormField label="Venue Name" required>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. The Crown Bar"
                  className="w-full text-sm text-slate-800 placeholder:text-slate-400 outline-none"
                />
              </FormField>
              <div className="h-px bg-slate-100 ml-4" />
              <FormField label="Venue Type">
                <select
                  value={venueType}
                  onChange={(e) => setVenueType(e.target.value)}
                  className="w-full text-sm text-slate-800 outline-none bg-transparent cursor-pointer"
                >
                  {VENUE_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </FormField>
              <div className="h-px bg-slate-100 ml-4" />
              <div className="px-4 py-3">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">
                  Town *
                </p>
                <TownSelect value={town} onChange={setTown} />
              </div>
              <div className="h-px bg-slate-100 ml-4" />
              <FormField label="Address">
                <input
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="123 High Street"
                  className="w-full text-sm text-slate-800 placeholder:text-slate-400 outline-none"
                />
              </FormField>
              <div className="h-px bg-slate-100 ml-4" />
              <FormField label="Postcode">
                <input
                  value={postcode}
                  onChange={(e) => setPostcode(e.target.value)}
                  placeholder="KA1 1AA"
                  className="w-full text-sm text-slate-800 placeholder:text-slate-400 outline-none"
                />
              </FormField>
              <div className="h-px bg-slate-100 ml-4" />
              <FormField label="Phone">
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="01292 265000"
                  className="w-full text-sm text-slate-800 placeholder:text-slate-400 outline-none"
                />
              </FormField>
              <div className="h-px bg-slate-100 ml-4" />
              <FormField label="Website">
                <input
                  type="url"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="https://yourbar.co.uk"
                  className="w-full text-sm text-slate-800 placeholder:text-slate-400 outline-none"
                />
              </FormField>
              <div className="h-px bg-slate-100 ml-4" />
              <FormField label="Description">
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Tell visitors what makes your venue great..."
                  rows={3}
                  className="w-full text-sm text-slate-800 placeholder:text-slate-400 outline-none resize-none leading-relaxed"
                />
              </FormField>
            </Section>

            <Section title="Cover Photo">
              <div className="px-4 py-3">
                <p className="text-xs text-slate-400 mb-3">
                  Upload a photo of your venue to attract more visitors.
                </p>
                <ImageUpload
                  value={coverPhoto}
                  onChange={setCoverPhoto}
                  onUploadingChange={setImageUploading}
                  label="Upload Photo"
                  stockCategory="business"
                />
              </div>
            </Section>

            <button
              type="button"
              disabled={imageUploading}
              onClick={() => {
                if (!name.trim()) {
                  alert("Please enter a venue name.");
                  return;
                }
                setStep(1);
              }}
              className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold py-4 rounded-2xl transition-colors disabled:opacity-60"
            >
              {imageUploading ? (
                <>
                  <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Uploading image…
                </>
              ) : (
                <>
                  Opening Hours <ChevronRight size={16} />
                </>
              )}
            </button>
          </>
        )}

        {/* ── STEP 2: Opening Hours ── */}
        {step === 1 && (
          <>
            <Section title="Opening Hours">
              <div className="px-4 py-2">
                <p className="text-xs text-slate-400 pt-1 pb-2 leading-relaxed">
                  Toggle a day closed if you don't open then. Times use a 24-hour clock.
                </p>
                <div className="space-y-1">
                  {DAY_NAMES.map((day, i) => {
                    const dh = openingHours[i]!;
                    return (
                      <div
                        key={day}
                        className={`flex items-center gap-3 py-2.5 border-b border-slate-100 last:border-0 ${
                          dh.closed ? "opacity-50" : ""
                        }`}
                      >
                        {/* Day name */}
                        <span className="text-sm font-semibold text-slate-700 w-24 flex-shrink-0">
                          {day}
                        </span>

                        {/* Closed toggle */}
                        <button
                          type="button"
                          onClick={() => updateHours(i, { closed: !dh.closed })}
                          className={`flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${
                            dh.closed
                              ? "bg-red-100 text-red-600"
                              : "bg-slate-100 text-slate-500"
                          }`}
                        >
                          {dh.closed ? "Closed" : "Open"}
                        </button>

                        {/* Times */}
                        {!dh.closed && (
                          <div className="flex items-center gap-1 ml-auto flex-shrink-0">
                            <TimeSelect
                              value={dh.open}
                              onChange={(v) => updateHours(i, { open: v })}
                            />
                            <span className="text-slate-400 text-xs">to</span>
                            <TimeSelect
                              value={dh.close}
                              onChange={(v) => updateHours(i, { close: v })}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </Section>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setStep(0)}
                className="flex items-center justify-center gap-1 border border-slate-200 text-slate-600 py-4 px-5 rounded-2xl text-sm hover:bg-slate-50 transition-colors"
              >
                <ChevronLeft size={16} /> Back
              </button>
              <button
                type="button"
                onClick={() => setStep(2)}
                className="flex-1 flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold py-4 rounded-2xl transition-colors"
              >
                What You Offer <ChevronRight size={16} />
              </button>
            </div>
          </>
        )}

        {/* ── STEP 3: What You Offer ── */}
        {step === 2 && (
          <>
            {/* Amenities */}
            <Section title="Amenities">
              <div className="px-4 py-3">
                <div className="grid grid-cols-2 gap-2">
                  {AMENITY_OPTIONS.map((a) => {
                    const active = amenities.has(a.key);
                    return (
                      <button
                        key={a.key}
                        type="button"
                        onClick={() => toggleAmenity(a.key)}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-medium text-left transition-all ${
                          active
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                        }`}
                      >
                        <span
                          className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border transition-all ${
                            active
                              ? "bg-primary border-primary"
                              : "border-slate-300"
                          }`}
                        >
                          {active && <Check size={10} className="text-white" />}
                        </span>
                        <span className="leading-tight text-xs">{a.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </Section>

            {/* Regular Events */}
            <Section title="Regular Events">
              <div className="divide-y divide-slate-100">
                {EVENT_DEFINITIONS.map((def) => {
                  const ev = events[def.key]!;
                  return (
                    <div key={def.key} className="px-4 py-3">
                      {/* Event toggle header */}
                      <div className="flex items-center gap-3">
                        <span
                          className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            ev.enabled
                              ? "bg-primary/20 text-primary"
                              : "bg-slate-100 text-slate-400"
                          }`}
                        >
                          {def.icon}
                        </span>
                        <span
                          className={`flex-1 text-sm font-semibold ${
                            ev.enabled ? "text-slate-800" : "text-slate-500"
                          }`}
                        >
                          {def.label}
                        </span>
                        {/* Toggle */}
                        <button
                          type="button"
                          onClick={() => updateEvent(def.key, { enabled: !ev.enabled })}
                          className={`relative w-10 h-6 rounded-full transition-colors flex-shrink-0 ${
                            ev.enabled ? "bg-primary" : "bg-slate-200"
                          }`}
                        >
                          <span
                            className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                              ev.enabled ? "translate-x-5" : "translate-x-1"
                            }`}
                          />
                        </button>
                      </div>

                      {/* Expanded details */}
                      {ev.enabled && (
                        <div className="mt-3 space-y-3 pl-10">
                          {/* Frequency */}
                          <div>
                            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
                              Frequency
                            </p>
                            <div className="flex gap-1.5">
                              {(
                                [
                                  { v: "weekly", l: "Weekly" },
                                  { v: "fortnightly", l: "Fortnightly" },
                                  { v: "monthly", l: "Monthly" },
                                ] as const
                              ).map(({ v, l }) => (
                                <button
                                  key={v}
                                  type="button"
                                  onClick={() =>
                                    updateEvent(def.key, { frequency: v })
                                  }
                                  className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
                                    ev.frequency === v
                                      ? "border-primary bg-primary/10 text-primary"
                                      : "border-slate-200 text-slate-500"
                                  }`}
                                >
                                  {l}
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Day selector */}
                          <div>
                            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
                              Day(s)
                            </p>
                            <div className="flex gap-1 flex-wrap">
                              {SHORT_DAY_NAMES.map((dName, di) => {
                                const active = ev.days.includes(di);
                                return (
                                  <button
                                    key={di}
                                    type="button"
                                    onClick={() => toggleEventDay(def.key, di)}
                                    className={`w-9 h-9 rounded-full text-xs font-bold border transition-colors ${
                                      active
                                        ? "border-primary bg-primary text-white"
                                        : "border-slate-200 text-slate-500 hover:border-slate-300"
                                    }`}
                                  >
                                    {dName}
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                          {/* Time */}
                          <div>
                            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
                              Start Time
                            </p>
                            <div className="flex items-center gap-2 border border-slate-200 rounded-xl px-3 py-2 w-fit">
                              <Clock size={13} className="text-slate-400" />
                              <TimeSelect
                                value={ev.time}
                                onChange={(v) => updateEvent(def.key, { time: v })}
                              />
                            </div>
                          </div>

                          {/* Price */}
                          <div>
                            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
                              Entry Price (optional)
                            </p>
                            <input
                              value={ev.priceText}
                              onChange={(e) =>
                                updateEvent(def.key, { priceText: e.target.value })
                              }
                              placeholder="e.g. Free, £5 on door"
                              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 outline-none"
                            />
                          </div>

                          {/* Notes */}
                          <div>
                            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
                              Notes (optional)
                            </p>
                            <input
                              value={ev.notes}
                              onChange={(e) =>
                                updateEvent(def.key, { notes: e.target.value })
                              }
                              placeholder="Any extra details..."
                              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 outline-none"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Section>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="flex items-center justify-center gap-1 border border-slate-200 text-slate-600 py-4 px-5 rounded-2xl text-sm hover:bg-slate-50 transition-colors"
              >
                <ChevronLeft size={16} /> Back
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={handleSubmit}
                className="flex-1 flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold py-4 rounded-2xl transition-colors disabled:opacity-60"
              >
                {submitting ? (
                  <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Users size={16} /> Submit Venue
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
